import { useMemo, useState } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Checkbox, InputAdornment, Typography, TextField } from "@mui/material";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { useGetOne, useRefresh } from "react-admin";
import { DrivingSchool, DrivingSchoolLabel } from "../providers/drivingSchoolsProvider";
import { Row } from "./Row";
import { Label } from "./Label";
import { LoadingIndicator } from "./LoadingIndicator";
import { Column } from "./Column";

interface StudentLabelsMenuProps {
  student: Student;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}
export function StudentLabelsMenu({ student, anchorEl, open, onClose }: StudentLabelsMenuProps) {
  const refresh = useRefresh();
  const { data: drivingSchool } = useGetOne<DrivingSchool>("drivingSchools", {
    id: student.drivingSchoolId,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filterValue, setFilterValue] = useState<string>("");

  const handleLabelClick = (label: DrivingSchoolLabel) => async () => {
    setIsLoading(true);
    const isAssignedLabel = student.assignedLabels.includes(label.id);
    await studentsProvider.update("students", {
      id: student.id,
      data: {
        assignedLabels: isAssignedLabel
          ? student.assignedLabels.filter((id) => id !== label.id)
          : [...student.assignedLabels, label.id],
      },
      previousData: student,
    });
    showSuccessToast(
      isAssignedLabel
        ? `Die Zuweisung des Labels ${label.name} zu Fahrschüler ${student.name} wurde erfolgreich aufgehoben`
        : `Label ${label.name} wurde dem Fahrschüler ${student.name} erfolgreich zugewiesen`,
    );
    refresh();
    setIsLoading(false);
    onClose();
    setFilterValue("");
  };

  const drivingSchoolLabels = Object.values(drivingSchool?.labels || {}).sort((a, b) => a.name.localeCompare(b.name));

  const filteredDrivingSchoolLabels = useMemo(
    () => drivingSchoolLabels.filter(({ name }) => name.toLowerCase().includes(filterValue)),
    [drivingSchoolLabels, filterValue],
  );

  return (
    <Menu
      id="labels-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <ListSubheader sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: "32px", color: "text.primary" }}>
        Labels verwalten
      </ListSubheader>
      {isLoading ? (
        <Box sx={{ height: `${32 * drivingSchoolLabels.length}px` }}>
          <LoadingIndicator />
        </Box>
      ) : filteredDrivingSchoolLabels.length === 0 ? (
        <Typography variant="body2" sx={{ p: 1, textAlign: "center" }}>
          Keine labels
        </Typography>
      ) : (
        <Column>
          <Row px={2} mt={1.5} mb={1}>
            <TextField
              sx={{
                borderRadius: 8,
              }}
              size="small"
              placeholder="Suche"
              value={filterValue}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilterValue(event.target.value)}
              autoFocus
              InputProps={{
                sx: {
                  "& .MuiInputBase-input": {
                    fontSize: "16px",
                  },
                },
                startAdornment: (
                  <InputAdornment position="end" sx={{ marginLeft: -0.5, paddingRight: 1 }}>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Row>
          {filteredDrivingSchoolLabels.map((label) => (
            <MenuItem key={label.id} onClick={handleLabelClick(label)}>
              <Row alignItems="center" gap={1.5}>
                <Checkbox size="small" checked={student.assignedLabels.some((id) => id === label.id)} sx={{ p: 0 }} />
                <Label label={label} hideTooltip />
              </Row>
            </MenuItem>
          ))}
        </Column>
      )}
    </Menu>
  );
}
